import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  formatAdditionalDataKey,
  formatAppointmentStatus,
  formatConsultationTypeLabel,
  formatStaffName,
  getConsultationTypeCategory,
  parseAppointmentAdditionalData,
  parseAppointmentStartTime,
} from "pages/patient/PatientDetailsPage/components/Appointments/utils/formatAppointment";

import { useDropdownPosition } from "components/ui/DataTable/hooks/useDropdownPosition";

import s from "./AppointmentDetailsHoverCard.module.css";

const CARD_WIDTH = 520;

const STATUS_CLASS = {
  ARRIVED: s.statusArrived,
  IN_CONSULTATION: s.statusArrived,
  COMPLETED: s.statusCompleted,
  NO_SHOW: s.statusNoShow,
  CANCELLED: s.statusNoShow,
};

const TYPE_CLASS = {
  video: s.typeBadgeVideo,
  phone: s.typeBadgePhone,
  inPerson: s.typeBadgeInPerson,
};

export const AppointmentDetailsHoverCard = ({ appointment, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef(null);
  const cardRef = useRef(null);

  const { position, updatePosition } = useDropdownPosition(
    triggerRef,
    isOpen,
    { align: "left", menuWidth: CARD_WIDTH, menuRef: cardRef }
  );

  const handleMouseEnter = () => {
    updatePosition();
    setIsOpen(true);
  };

  const handleMouseLeave = () => setIsOpen(false);

  const start = parseAppointmentStartTime(appointment.startTime);
  const additionalData = parseAppointmentAdditionalData(
    appointment.additionalData
  );
  const reasonForVisit = appointment.reasonForVisit;
  const reasonDescription = appointment.reasonDescription;
  const cancellationReason = appointment.cancellationReason;
  const serviceName =
    appointment.practiceService?.name || appointment.serviceName;

  return (
    <span
      ref={triggerRef}
      className={s.trigger}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      {children}
      {isOpen &&
        createPortal(
          <div
            ref={cardRef}
            role="tooltip"
            className={s.card}
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: CARD_WIDTH,
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            {!!appointment.appointmentStatus && (
              <span
                className={`${s.statusBadge} ${
                  STATUS_CLASS[appointment.appointmentStatus] || s.statusDefault
                }`}
              >
                {formatAppointmentStatus(appointment.appointmentStatus)}
              </span>
            )}

            <div className={s.header}>
              <div className={s.headerMain}>
                <span className={s.headerLabel}>Appointment</span>
                <h4 className={s.headerDate}>
                  {start ? start.format("ddd, DD MMM YYYY") : "—"}
                </h4>
                <p className={s.headerTime}>
                  {start ? start.format("HH:mm") : "—"}
                  {!!appointment.durationInMins && (
                    <span className={s.headerDuration}>
                      · {appointment.durationInMins} mins
                    </span>
                  )}
                </p>
              </div>
              {!!appointment.consultationType && (
                <span
                  className={`${s.typeBadge} ${
                    TYPE_CLASS[getConsultationTypeCategory(
                      appointment.consultationType
                    )]
                  }`}
                >
                  {formatConsultationTypeLabel(appointment.consultationType)}
                </span>
              )}
            </div>

            <div className={s.infoGrid}>
              <div className={s.infoItem}>
                <span className={s.infoLabel}>Staff Member</span>
                <span className={s.infoValue}>
                  {formatStaffName(appointment.assignedToStaffMember)}
                </span>
              </div>
              {!!serviceName && (
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Service</span>
                  <span className={s.infoValue}>{serviceName}</span>
                </div>
              )}
              {!!appointment.location?.name && (
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Location</span>
                  <span className={s.infoValue}>{appointment.location.name}</span>
                </div>
              )}
              {!!appointment.companyName && (
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Company</span>
                  <span className={s.infoValue}>{appointment.companyName}</span>
                </div>
              )}
              {!!appointment.telephoneNumber && (
                <div className={s.infoItem}>
                  <span className={s.infoLabel}>Telephone</span>
                  <span className={s.infoValue}>
                    {appointment.telephoneNumber}
                  </span>
                </div>
              )}
            </div>

            {!!(reasonForVisit || reasonDescription || cancellationReason) && (
              <div className={s.reasonSection}>
                {!!reasonForVisit && (
                  <div className={s.reasonItem}>
                    <span className={s.infoLabel}>Reason for Visit</span>
                    <span className={s.reasonValue}>{reasonForVisit}</span>
                  </div>
                )}
                {!!reasonDescription && (
                  <div className={s.reasonItem}>
                    <span className={s.infoLabel}>Reason Description</span>
                    <p className={s.reasonDescription}>{reasonDescription}</p>
                  </div>
                )}
                {!!cancellationReason && (
                  <div className={s.reasonItem}>
                    <span className={s.infoLabel}>Cancellation Reason</span>
                    <p className={s.reasonDescription}>{cancellationReason}</p>
                  </div>
                )}
              </div>
            )}

            {!!additionalData && (
              <div className={s.additionalDataGrid}>
                {Object.entries(additionalData).map(([key, value]) => (
                  <div key={key} className={s.infoItem}>
                    <span className={s.infoLabel}>
                      {formatAdditionalDataKey(key)}
                    </span>
                    <span className={s.infoValue}>
                      {typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}
    </span>
  );
};
